import React from 'react';
import { v4 as uuid } from 'uuid';
import { Link } from 'react-router-dom';
import { PropertyType } from '../../../common';
import styles from './RowProvider.module.scss';
import {
	CheckmarkGreenFull,
	DangerDashFill,
	DangerFill,
	Desktop,
	Mobile,
	TimeWarning,
	Tooltip,
	WarningRound,
} from 'esa-ui-library';
import { transactionsListMap } from './PlayerTransaction.const';
import { find } from 'lodash';
import wallet from '../../../common/images/wallet.svg';
import arrows from '../../../common/images/arrow-path-rounded-square.svg';
import history from '../../../common/images/history_icon.svg';

export const getRows = (
	transactions: PropertyType[],
	columns: string[],
	user: PropertyType,
	onSelect: (action: string, session: PropertyType) => void
): PropertyType[] => {
	const iconStatusesMap: PropertyType = {
		SUCCESS: <CheckmarkGreenFull />,
		FAILED: <DangerFill />,
		PENDING: <TimeWarning />,
		'SESSION ALREADY CLOSED': <WarningRound />,
		'INSUFICIENT CREDIT': <DangerDashFill />,
		'INSUFFICIENT CREDIT': <DangerDashFill />,
		UNKNOWN: <WarningRound />,
	};

	const getActiveCells = (s: PropertyType): PropertyType[] => {
		const rows: PropertyType[] = [];
		columns.forEach((column) => {
			const field = find(transactionsListMap, ['label', column])?.key;
			const showHistory =
				s.game_provider_id === 2000 && (s.type_name === 'BET' || s.type_name === 'WIN') && s.status_name === 'SUCCESS';
			let mayFail = false;
			let maySucceed = false;
			if (user?.permissions?.canHandleTransactions) {
				if (s?.type === 1019) {
					maySucceed = false;
					mayFail = s?.status === 1;
				} else if (s?.type === 1022) {
					maySucceed = s?.status === 1 || s?.status >= 3;
					mayFail = s?.status === 1;
				} else if (s?.type === 1016) {
					maySucceed = s?.status === 1;
					mayFail = false;
				} else if (s?.game_provider_id === 1) {
					mayFail = maySucceed = s?.status === 1;
				}
			}

			const cellsMap = {
				transaction_id: <span key={uuid()}>{s.transaction_id}</span>,
				session_id: (
					<span key={uuid()}>
						<Link
							to={`/players/player-sessions?session_id=${s.session_id}`}
							rel='noopener noreferrer'
							className={styles.Link}
						>
							{s.session_id}
						</Link>
					</span>
				),
				player_id: (
					<span key={uuid()}>
						<Link
							to={`/players/player-list/${s.player_id}`}
							target='_blank'
							rel='noopener noreferrer'
							className={styles.Link}
						>
							{s.player_id}
						</Link>
					</span>
				),
				external_player_id: <span key={uuid()}>{s.external_player_id}</span>,
				partner: <span key={uuid()}>{s.partner}</span>,
				skin: <span key={uuid()}>{s.skin}</span>,
				thirdparty_trx_id: <span key={uuid()}>{s.thirdparty_trx_id}</span>,
				provider: <span key={uuid()}>{s.provider}</span>,
				status_name: (
					<span key={uuid()} className={styles.DataIcon}>
						<Tooltip position='top' text={s.status_name}>
							<span className={styles.Status}>{iconStatusesMap[s.status_name] || iconStatusesMap['UNKNOWN']}</span>
						</Tooltip>
						{(mayFail || maySucceed) && <span className={styles.Divider}>|</span>}
						{mayFail && (
							<span
								className={styles.Status}
								onClick={() => {
									if (window.confirm('Are you sure you want to mark this transaction as FAILED?')) {
										onSelect('fail', s);
									}
								}}
							>
								{iconStatusesMap['FAILED']}{' '}
							</span>
						)}
						{maySucceed && (
							<span
								className={styles.Status}
								onClick={() => {
									if (window.confirm('Are you sure you want to mark this transaction as SUCCESS?')) {
										onSelect('succeed', s);
									}
								}}
							>
								{iconStatusesMap['SUCCESS']}{' '}
							</span>
						)}
					</span>
				),
				game_id: <span key={uuid()}>{s.game_id}</span>,
				device: (
					<span key={uuid()} className={styles.DataIcon}>
						<Tooltip position='top' text={s.device}>
							{s.device === 'desktop' ? <Desktop /> : <Mobile />}
						</Tooltip>
					</span>
				),
				currency: <span key={uuid()}>{s.currency}</span>,
				amount: (
					<span key={uuid()} className={styles.DataAmount}>
						{s.amount}
					</span>
				),
				orig_amount: (
					<span key={uuid()} className={styles.DataAmount}>
						{s.orig_amount}
					</span>
				),
				balance: (
					<span key={uuid()} className={styles.DataAmount}>
						{s.balance}
					</span>
				),
				type_name: <span key={uuid()}>{`${s.type_name} ${s.context && `(${s.context})`}`}</span>,
				wallet: (
					<span key={uuid()}>
						{s.wallet ? (
							<Tooltip position='top' text={s.wallet} className={styles.DataIcon}>
								<img src={s.wallet === 'seamless' ? arrows : wallet} alt='source' width='25' />
							</Tooltip>
						) : (
							'- -'
						)}
					</span>
				),
				createtime: <span key={uuid()}>{s.createtime}</span>,
				finishtime: <span key={uuid()}>{s.finishtime}</span>,
				aams_ticket_id: <span key={uuid()}>{s.aams_ticket_id ? s.aams_ticket_id : ''}</span>,
				hand_details: (
					<span key={uuid()} onClick={() => onSelect('round_history', s)}>
						{showHistory && (
							<div>
								<Tooltip position='top' text='Round history'>
									<img src={history} alt='history' width={22} height={22} />
								</Tooltip>
							</div>
						)}
					</span>
				),
			};
			if (field) {
				rows.push((cellsMap as Record<string, JSX.Element>)[field]);
			}
		});
		return rows;
	};

	const data = transactions.map((s) => getActiveCells(s));
	return data;
};
